import React, { useEffect, useState } from 'react';
import { Award, Globe, TrendingUp, Languages } from 'lucide-react';
interface AboutProps {
  language: 'en' | 'es';
}
function CountUpAnimation({
  end,
  duration = 2000
}: {
  end: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  return <span data-id="element-20">{count}</span>;
}
export function About({
  language
}: AboutProps) {
  const content = {
    en: {
      title: 'About Sparrow Advance',
      subtitle: 'Funding the backbone of America — real entrepreneurs like you.',
      description: 'Sparrow Advance helps small and medium business owners nationwide secure quick, transparent, and reliable funding — without the stress of traditional banking. We speak English and Spanish.',
      mission: 'Our mission is to empower entrepreneurs with the capital they need to grow their businesses. We provide exceptional service, maintain complete transparency, and treat every client with respect and dignity.',
      badge: 'Se Habla Español',
      stats: [{
        icon: Award,
        value: 1000,
        suffix: '+',
        label: 'Businesses Funded'
      }, {
        icon: Globe,
        value: 50,
        prefix: '$',
        suffix: 'M+',
        label: 'Total Funding Provided'
      }, {
        icon: TrendingUp,
        value: 24,
        suffix: 'hrs',
        label: 'Average Funding Time'
      }]
    },
    es: {
      title: 'Sobre Sparrow Advance',
      subtitle: 'Financiando la columna vertebral de América — empresarios reales como usted.',
      description: 'Sparrow Advance ayuda a propietarios de pequeñas y medianas empresas en todo el país a obtener financiamiento rápido, transparente y confiable — sin el estrés de la banca tradicional. Hablamos inglés y español.',
      mission: 'Nuestra misión es empoderar a los empresarios con el capital que necesitan para hacer crecer sus negocios. Brindamos servicio excepcional, mantenemos transparencia completa y tratamos a cada cliente con respeto y dignidad.',
      badge: 'We Speak English',
      stats: [{
        icon: Award,
        value: 1000,
        suffix: '+',
        label: 'Negocios Financiados'
      }, {
        icon: Globe,
        value: 50,
        prefix: '$',
        suffix: 'M+',
        label: 'Financiamiento Total Proporcionado'
      }, {
        icon: TrendingUp,
        value: 24,
        suffix: 'hrs',
        label: 'Tiempo Promedio de Financiamiento'
      }]
    }
  };
  const t = content[language];
  return <section id="about" className="w-full py-20 bg-gradient-to-br from-[#0A1929] via-[#142B44] to-[#1A365D] text-white relative overflow-hidden" data-id="element-21">
      <div className="absolute inset-0" data-id="element-22">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2D5D9F] rounded-full filter blur-3xl opacity-20" data-id="element-23"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#4A90E2] rounded-full filter blur-3xl opacity-20" data-id="element-24"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25">
        <div className="text-center mb-12" data-id="element-26">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-id="element-27">{t.title}</h2>
          <p className="text-2xl text-gray-200 mb-8" data-id="element-28">{t.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-29">
          <div data-id="element-30">
            <p className="text-xl mb-6 leading-relaxed text-gray-200" data-id="element-31">
              {t.description}
            </p>
            <div className="inline-flex items-center space-x-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-xl border-2 border-white/30 mb-6" data-id="element-32">
              <Languages className="text-white" size={24} data-id="element-33" />
              <span className="text-lg font-bold text-white" data-id="element-34">{t.badge}</span>
            </div>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-35">{t.mission}</p>
          </div>
          <div className="relative" data-id="element-36">
            <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800" alt="Business team" className="rounded-2xl shadow-2xl border-4 border-white/10" data-id="element-37" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-38">
          {t.stats.map((stat, index) => <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105" data-id="element-39">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4" data-id="element-40">
                <stat.icon size={32} data-id="element-41" />
              </div>
              <div className="text-4xl font-bold mb-2" data-id="element-42">
                {stat.prefix}
                <CountUpAnimation end={stat.value} data-id="element-43" />
                {stat.suffix}
              </div>
              <div className="text-gray-300 font-medium" data-id="element-44">{stat.label}</div>
            </div>)}
        </div>
      </div>
    </section>;
}